import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const EMPTY = { shareholder_name: "", ownership_percentage: "", effective_from: "", effective_to: "" };

export default function OwnershipPage() {
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const res = await api.get("/companies");
    setCompanies(res.data);
    if (!companyId && res.data.length > 0) setCompanyId(res.data[0].id);
  };

  const loadRecords = async (cid) => {
    const res = await api.get("/ownership", { params: { company_id: cid } });
    setRecords(res.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId) loadRecords(companyId).catch((e) => toast.error(apiError(e)));
  }, [companyId]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      shareholder_name: r.shareholder_name,
      ownership_percentage: String(r.ownership_percentage),
      effective_from: r.effective_from,
      effective_to: r.effective_to || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = {
      shareholder_name: form.shareholder_name,
      ownership_percentage: Number(form.ownership_percentage),
      effective_from: form.effective_from,
      effective_to: form.effective_to || null,
    };
    try {
      if (editing) {
        await api.patch(`/ownership/${editing.id}`, payload);
        toast.success("Ownership updated");
      } else {
        await api.post("/ownership", { ...payload, company_id: companyId });
        toast.success("Ownership record created");
      }
      setDialogOpen(false);
      await loadRecords(companyId);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const currentTotal = records
    .filter((r) => {
      const today = new Date().toISOString().slice(0, 10);
      return r.effective_from <= today && (!r.effective_to || r.effective_to >= today);
    })
    .reduce((sum, r) => sum + r.ownership_percentage, 0);

  return (
    <div data-testid="ownership-page">
      <PageHeader
        title="Ownership"
        subtitle="Shareholder structure per company — effective-dated"
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-64" data-testid="ownership-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Button onClick={openCreate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="new-ownership-button">
                <Plus className="h-4 w-4 mr-1.5" /> New ownership record
              </Button>
            )}
          </div>
        }
      />
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden max-w-4xl" data-testid="ownership-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Shareholder</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Ownership</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Effective from</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Effective to</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`ownership-row-${r.id}`}>
                <td className="py-3 px-4 text-sm font-semibold text-slate-800">{r.shareholder_name}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{r.ownership_percentage} %</td>
                <td className="py-3 px-4 text-sm text-slate-700">{r.effective_from}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{r.effective_to || "Open"}</td>
                <td className="py-3 px-4 text-right">
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`edit-ownership-${r.id}`}>Edit</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {records.length === 0 && (
          <EmptyState title="No ownership records" description="Add shareholders for the selected company." testId="ownership-empty" />
        )}
        {records.length > 0 && (
          <div className="px-4 py-3 bg-secondary/50 border-t border-slate-200 text-xs text-slate-600 flex items-center gap-2" data-testid="ownership-total">
            Current total ownership: <span className="font-semibold text-slate-900">{Math.round(currentTotal * 100) / 100} %</span>
            {Math.abs(currentTotal - 100) < 0.001 ? (
              <StatusBadge tone="green" testId="ownership-complete-badge">Complete</StatusBadge>
            ) : (
              <StatusBadge tone="amber" testId="ownership-incomplete-badge">Incomplete — must total 100% before use in dividend calculations</StatusBadge>
            )}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="ownership-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit ownership" : "New ownership record"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Shareholder name</Label>
              <Input value={form.shareholder_name} onChange={(e) => setForm({ ...form, shareholder_name: e.target.value })} data-testid="ownership-shareholder-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Ownership percentage</Label>
              <Input type="number" step="0.01" min="0" max="100" value={form.ownership_percentage} onChange={(e) => setForm({ ...form, ownership_percentage: e.target.value })} data-testid="ownership-percentage-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Effective from</Label>
                <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="ownership-effective-from-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Effective to (optional)</Label>
                <Input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} data-testid="ownership-effective-to-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="ownership-cancel-button">Cancel</Button>
            <Button onClick={save} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="ownership-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
